import { ArrowLeftOutlined, CloseCircleOutlined } from "@ant-design/icons"
import { Tag } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { RoutesPaths } from "../../../constants/routes.ts"

interface ProjectContextBannerProps {
  projectId: string
  projectName: string
  /** Where "Clear filter" lands — the module's own bare global route, with
   * no `?project=` param, never a `navigate(-1)`: a customer who arrived
   * here from a bookmark or a fresh tab has no "back" to fall to. */
  clearHref: string
}

/** The one contextual-filter strip every global module (Documents, Photos,
 * Calendar, Messages, Payments) shows when opened pre-filtered to a project
 * — from Project Overview's own links, an old project-tab deep link, or a
 * typed `?project=` URL. Names the project, and offers both ways out: back
 * to that project's own workspace, or stay on this module but see every
 * project again. Never rendered for a plain sidebar visit (no `?project=`
 * at all) — that must always open the true global, unfiltered view. */
export function ProjectContextBanner({ projectId, projectName, clearHref }: ProjectContextBannerProps) {
  const { t } = useTranslation()
  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-primary/20 bg-primary/5 px-4 py-2.5">
      <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
        <span>{t("portal.contextFilter.showingFor")}</span>
        <Tag color="blue" className="m-0">{projectName}</Tag>
      </div>
      <div className="flex items-center gap-4 text-sm">
        <Link
          to={RoutesPaths.portal.project(projectId)}
          className="flex items-center gap-1 text-primary hover:underline"
        >
          <ArrowLeftOutlined />
          {t("portal.contextFilter.backToProject")}
        </Link>
        <Link to={clearHref} className="flex items-center gap-1 text-gray-500 hover:text-gray-700">
          <CloseCircleOutlined />
          {t("portal.contextFilter.clear")}
        </Link>
      </div>
    </div>
  )
}
